import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Pusher, { Channel } from 'pusher-js';
import { PUSHER_STATES, PUSHER_STATE_MESSAGES, PusherContextState, defaultState } from './PusherContextConstants';
import { getEnvConfig } from '@/constants';
import {
  isFullVideoFaceRecEnabled,
  isFullVideoFaceRecForPreRecordedEnabled,
  isSimplifiedUploadEnabled
} from '@/stores/clip';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import useDownloadToasts from '@/hooks/useDownloadToasts';
import { loadContentDownloadsList } from '@/stores/downloadsStore';

const PusherContext = createContext<PusherContextState>(defaultState);

// A new context for pusher is required to keep the implementation clean and separate from the main context
export function PusherContextProvider({ children, organizationId, userId, logger, ...restProps }) {
  const orgChannelRef = useRef<Channel | null>(null);
  const userChannelRef = useRef<Channel | null>(null);
  const pusherRef = useRef<Pusher | null>(null);
  const [isPusherConnected, setIsPusherConnected] = useState(false);

  const { showClipDownloadToastFromId, showTextAssetDownloadToast, showTextAssetSocketFailureToast } =
    useDownloadToasts();

  const handleOrgPushMessage = (state, data) => {
    logger.info('Org Pusher message received', state);

    // TODO @dusangc: Remove second part of the condition once socket for Saved Search is handled on the BE
    const shouldDisableToasts =
      window.location.pathname.includes(data?.upload_id) || (!data.broadcast_id && !data.upload_id);

    // Org-level messages identify the source by upload_id (uploads) vs broadcast_id (pre-recorded
    // events). Pick the full-video flag accordingly so the FULLVIDEO done-state is honored for
    // pre-recorded events when their own flag is on, mirroring uploads.
    const isFullVideoFaceRec = data.upload_id ? isFullVideoFaceRecEnabled() : isFullVideoFaceRecForPreRecordedEnabled();
    const generationDoneState =
      data.media_type === 'AUDIO'
        ? PUSHER_STATES.CLIP_GENERATION_DONE
        : isFullVideoFaceRec
          ? PUSHER_STATES.FTUX_FULLVIDEO_FACIAL_RECOGNITION_DONE
          : PUSHER_STATES.FTUX_CLIPS_FACIAL_RECOGNITION_DONE;

    const screenShareDetectionStatusNotProcessingStates = [
      PUSHER_STATES.SCREENSHARE_DETECTION_DONE,
      PUSHER_STATES.SCREENSHARE_DETECTION_FAILED,
      PUSHER_STATES.SCREENSHARE_DETECTION_UNAVAILABLE
    ];

    if (
      [
        PUSHER_STATES.TRANSCRIPTION_DONE,
        PUSHER_STATES.TRANSCRIPTION_FAILED,
        PUSHER_STATES.TRANSCRIPTION_INVALID_AUDIO,
        PUSHER_STATES.TRANSCRIPTION_NO_AUDIOSTREAM,
        PUSHER_STATES.TRANSCRIPTION_UNAVAILABLE,
        PUSHER_STATES.TRANSCRIPTION_SUPPORT_RESTRICTED
      ].includes(state)
    ) {
      data.state = state;
      EventBus.dispatch(CustomEvents.TranscriptionStatusUpdated, data);

      if (shouldDisableToasts) {
        return;
      }

      if (state !== PUSHER_STATES.TRANSCRIPTION_DONE) {
        showErrorToast(PUSHER_STATE_MESSAGES[state]);
      }
    } else if (
      [
        generationDoneState,
        PUSHER_STATES.CLIP_GENERATION_FAILED,
        PUSHER_STATES.CLIP_GENERATION_LIMIT_REACHED,
        ...screenShareDetectionStatusNotProcessingStates
      ].includes(state)
    ) {
      data.state = state;
      EventBus.dispatch(CustomEvents.ClipGenerationStatusUpdated, data);

      if (screenShareDetectionStatusNotProcessingStates.includes(state)) {
        EventBus.dispatch(CustomEvents.ScreenshareDetectionStatusUpdated, data);
      }

      if (shouldDisableToasts || screenShareDetectionStatusNotProcessingStates.includes(state)) {
        return;
      }

      if (state === generationDoneState) {
        showSuccessToast(PUSHER_STATE_MESSAGES[state]);
      } else {
        showErrorToast(PUSHER_STATE_MESSAGES[state]);
      }
    }

    if ([PUSHER_STATES.SOCIAL_POST_GENERATION_DONE].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.SocialPostGenerationStatusUpdated, data);
    }

    if ([PUSHER_STATES.BLOG_POST_GENERATION_DONE].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.BlogPostGenerationStatusUpdated, data);
    }

    if ([PUSHER_STATES.EMAIL_POST_GENERATION_DONE].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.EmailPostGenerationStatusUpdated, data);
    }

    if ([PUSHER_STATES.TAKEAWAY_POST_GENERATION_DONE].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.TakeawayPostGenerationStatusUpdated, data);
    }

    if ([PUSHER_STATES.EVENT_MIGRATION_COMPLETED].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.EventMigrationCompleted, {
        eventId: data.event_id,
        broadcastId: data.broadcast_id
      });
    }

    if (
      [
        PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_DONE,
        PUSHER_STATES.FACIAL_RECOGNITION_FAST_DONE,
        PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_PROCESSING,
        PUSHER_STATES.FACIAL_RECOGNITION_FAST_PROCESSING,
        PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_FAILED,
        PUSHER_STATES.FACIAL_RECOGNITION_FAST_FAILED,
        PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_PROCESSING,
        PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_DONE,
        PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_FAILED
      ].includes(state)
    ) {
      data.state = state;
      EventBus.dispatch(CustomEvents.FacialRecognitionStatusUpdated, data);
    }

    if ([PUSHER_STATES.CHAPTER_GENERATION_DONE, PUSHER_STATES.CHAPTER_GENERATION_FAILED].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.ChapterGenerationStatusUpdated, data);
    }

    if ([PUSHER_STATES.CAPTION_TRANSLATION_DONE, PUSHER_STATES.CAPTION_TRANSLATION_FAILED].includes(state)) {
      data.state = state;
      EventBus.dispatch(CustomEvents.CaptionTranslationStatusUpdated, data);
    }

    if (state === PUSHER_STATES.CL_HOMEPAGE_CAROUSEL_STATUS_UPDATED) {
      EventBus.dispatch(CustomEvents.HomepageCarouselStatusUpdated, data);
    }

    // Terminal states only — PROCESSING is intentionally ignored.
    if (
      [PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_DONE, PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_FAILED].includes(state)
    ) {
      data.state = state;
      EventBus.dispatch(CustomEvents.HighlightReelGenerationStatusUpdated, data);
    }

    // Simplified upload flow: dispatch ClipGenerationStatusUpdated for CLIP_GENERATION_DONE so
    // useUploadEditorReadiness can track when AI clips are ready. The legacy flow uses FTUX facial-rec
    // events as the "generation done" signal for video, so CLIP_GENERATION_DONE is otherwise
    // unhandled for video content.
    if (state === PUSHER_STATES.CLIP_GENERATION_DONE && isSimplifiedUploadEnabled()) {
      data.state = state;
      EventBus.dispatch(CustomEvents.ClipGenerationStatusUpdated, data);
    }
  };

  const handleUserPushMessage = (state, data) => {
    logger.info('User Pusher message received', state);

    if (state === PUSHER_STATES.DOWNLOAD_DONE) {
      showClipDownloadToastFromId(data.id);
      loadContentDownloadsList(data.broadcast_id || data.upload_id, data.saved_search_id);
    } else if (state === PUSHER_STATES.DOWNLOAD_FAILED) {
      showClipDownloadToastFromId(data.id, false);
      loadContentDownloadsList(data.broadcast_id || data.upload_id, data.saved_search_id);
    } else if (state === PUSHER_STATES.MULTIMODAL_DOWNLOAD_DONE) {
      showTextAssetDownloadToast(data);
      loadContentDownloadsList(data.broadcast_id || data.upload_id, data.saved_search_id);
    } else if (state === PUSHER_STATES.MULTIMODAL_DOWNLOAD_FAILED) {
      showTextAssetSocketFailureToast(data);
      loadContentDownloadsList(data.broadcast_id || data.upload_id, data.saved_search_id);
    } else if (
      [PUSHER_STATES.PUBLISH_STATUS_UPDATED_DONE, PUSHER_STATES.PUBLISH_STATUS_UPDATED_FAILED].includes(state)
    ) {
      data.state = state;
      EventBus.dispatch(CustomEvents.PublishRecordingStatusUpdated, { data });
    }
  };

  useEffect(() => {
    if (!organizationId || !userId || pusherRef.current) {
      return;
    }

    try {
      pusherRef.current = new Pusher(getEnvConfig('PUSHER_APP_KEY'), {
        cluster: getEnvConfig('PUSHER_APP_CLUSTER')
      });
    } catch (error) {
      logger.warn('Error initializing pusher', error);
      return;
    }

    orgChannelRef.current = pusherRef.current.subscribe(organizationId);
    userChannelRef.current = pusherRef.current.subscribe(userId);
    pusherRef.current.connection.bind('state_change', states => {
      setIsPusherConnected(!(states.current === 'failed' || states.current === 'unavailable'));
    });

    orgChannelRef.current.bind_global(handleOrgPushMessage);
    userChannelRef.current.bind_global(handleUserPushMessage);

    return () => {
      pusherRef.current?.unsubscribe(organizationId);
      pusherRef.current?.unsubscribe(userId);
      orgChannelRef.current?.unbind_global();
      userChannelRef.current?.unbind_global();
    };
  }, [organizationId, userId]);

  return (
    <PusherContext.Provider
      value={{
        isPusherConnected,
        orgChannel: orgChannelRef.current
      }}
    >
      {React.cloneElement(children, {
        ...restProps
      })}
    </PusherContext.Provider>
  );
}

export function usePusherContext() {
  return useContext(PusherContext);
}
